import useAxios from "./../../../Hooks/useAxios";
import { useEffect, useState } from "react";

// Icons
import {
  FiUser,
  FiMail,
  FiPhone,
  FiClipboard,
  FiFileText,
} from "react-icons/fi";

function DoctorNotes({ visits = [] }) {
  const [notesByVisit, setNotesByVisit] = useState({});
  const request = useAxios();

  const safe = (value) => value ?? "—";

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDoctorNotes = async () => {
    if (!visits?.length) return;

    const visitIds = visits.map((v) => v?.id).filter(Boolean);
    if (!visitIds.length) return;

    const res = await request({
      method: "GET",
      url: "chapchap/list/visit_notes_v2",
      params: { "visit_id-int": visitIds.join(",") },
      showSuccessModal: false,
      showLoadingModal: false,
    });

    if (!res?._error && res?.data?.docs?.length) {
      const grouped = res.data.docs.reduce((acc, note) => {
        const vid = note?.visits?.id ?? "unknown";
        if (!acc[vid]) acc[vid] = [];
        acc[vid].push(note);
        return acc;
      }, {});
      setNotesByVisit(grouped);
    }
  };

  useEffect(() => {
    getDoctorNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits]);

  if (!Object.keys(notesByVisit).length) {
    return (
      <div className="p-5 text-center text-auro_metal_saurus">
        No doctor notes available.
      </div>
    );
  }

  return (
    <div className="w-full p-5 flex flex-col gap-6">
      {Object.entries(notesByVisit).map(([visitId, notes]) => {
        const visit = notes[0]?.visits;

        return (
          <div
            key={visitId}
            className="bg-white shadow-xl rounded-3xl p-6 border border-bright_grey w-full"
          >
            {/* ===========================
                Card Title + Visit Date
            ============================ */}
            <div className="w-full flex justify-between items-center mb-4 pb-4 border-b border-bright_grey">
              <h2 className="text-2xl font-bold text-yankees_blue">
                Doctor Notes
              </h2>

              <span className="text-auro_metal_saurus text-sm">
                Visit Date: {formatDate(visit?.created_at)}
              </span>
            </div>

            {/* ===========================
                Doctor Info
            ============================ */}
            <div className="flex items-center gap-4 pb-4 mb-4 border-b border-bright_grey">
              <div className="w-16 h-16 rounded-full bg-ghost_white flex items-center justify-center text-3xl text-primary font-bold shadow-inner">
                {notes[0]?.team?.name?.[0] || "?"}
              </div>

              <div>
                <h2 className="text-2xl font-bold text-yankees_blue">
                  Dr. {safe(notes[0]?.team?.name)}
                </h2>

                <p className="text-auro_metal_saurus mt-1 flex items-center gap-1">
                  <FiMail /> {safe(notes[0]?.team?.email)}
                </p>

                <p className="text-auro_metal_saurus mt-1 flex items-center gap-1">
                  <FiPhone /> {safe(notes[0]?.team?.phone)}
                </p>
              </div>
            </div>

            {/* ===========================
                Visit Info
            ============================ */}
            <Section title="Visit Info">
              <Detail
                label="Payment Method"
                value={safe(visit?.default_payment_method)}
                icon={<FiClipboard />}
              />
              <Detail
                label="Checked In By"
                value={safe(notes[0]?.team?.name)}
                icon={<FiUser />}
              />
              <Detail
                label="Currently At"
                value={safe(visit?.currently_at)}
                icon={<FiUser />}
              />
            </Section>

            {/* ===========================
                Doctor Notes Fields
            ============================ */}
            {notes.some(
              (note) =>
                note?.symptoms ||
                note?.signs ||
                note?.hpi ||
                note?.rds ||
                note?.family_social_history ||
                note?.nutritional_history ||
                note?.general_examination ||
                note?.systemic_examination ||
                note?.diagnosis ||
                note?.investigation ||
                note?.final_diagnosis ||
                note?.treatment ||
                note?.gynecological_history ||
                note?.past_medical_history ||
                note?.systemic_review ||
                note?.impression ||
                note?.sign_and_symptoms
            ) && (
              <Section title="Doctor Notes Details">
                {notes.map((note) => {
                  const fields = {
                    Symptoms: note?.symptoms,
                    Signs: note?.signs,
                    HPI: note?.hpi,
                    RDS: note?.rds,
                    "Family & Social History": note?.family_social_history,
                    "Nutritional History": note?.nutritional_history,
                    "General Examination": note?.general_examination,
                    "Systemic Examination": note?.systemic_examination,
                    Diagnosis: note?.diagnosis,
                    Investigation: note?.investigation,
                    "Final Diagnosis": note?.final_diagnosis,
                    Treatment: note?.treatment,
                    "Gynecological History": note?.gynecological_history,
                    "Past Medical History": note?.past_medical_history,
                    "Systemic Review": note?.systemic_review,
                    Impression: note?.impression,
                    "Signs & Symptoms": note?.sign_and_symptoms,
                  };

                  const visibleFields = Object.entries(fields).filter(
                    ([, value]) => value
                  );
                  if (!visibleFields.length) return null;

                  return (
                    <div key={note?.id} className="mb-4">
                      <h4 className="text-lg font-semibold text-yankees_blue mb-2">
                        Note ID: {safe(note?.id)}
                      </h4>

                      <div className="flex flex-col gap-y-2">
                        {visibleFields.map(([label, value]) => (
                          <Detail
                            key={label}
                            label={label}
                            value={safe(value)}
                            icon={<FiFileText />}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </Section>
            )}
          </div>
        );
      })}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-4 p-4 bg-ghost_white rounded-2xl shadow-sm border border-bright_grey">
    <h3 className="text-lg font-semibold text-yankees_blue mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Detail = ({ label, value, icon }) => (
  <div className="flex justify-between items-start py-2 px-3 bg-white rounded-xl shadow border border-bright_grey">
    <div className="flex items-center gap-2 text-auro_metal_saurus">
      <span className="text-primary text-lg">{icon}</span>
      <span className="font-medium">{label}</span>
    </div>

    <div className="text-yankees_blue font-semibold text-right">{value}</div>
  </div>
);

export default DoctorNotes;
